import { Lazy } from "../lazy";
import { SqafSettingItem } from "./sqaf-setting-item";
import { ErrorHandler } from "../error-handler";
import { ServiceProvider } from "../provider/service-provider";
import { UrlHelper } from "../html/url-helper";
import { KoExtend } from "../../extend/knockout-extend";

type SettingRequest = string | ISettingRequest;

interface ISettingRequest {
	name: string;
	marketerId?: number;
}

interface ISettingResponse {
	name: string;
	marketerId: number;
	values: [any, any, any, any, any];
}

export class SqafSettingStorage {
	public static getInstance = Lazy.singleton(SqafSettingStorage);
	private static cacheKey = "settings";

	private static getSettingRequest(req: SettingRequest) {
		if (typeof req === "string") {
			req = { name: req };
		}

		if (!req.marketerId) {
			req.marketerId = UrlHelper.getUserMarketerId();
		}

		return req;
	}

	private items: KnockoutObservableArray<SqafSettingItem> = KoExtend.subscribing(ko.observableArray(this.loadSettingsFromLocalStorage()), this.storeSettingsIntoLocalStorage);

	public hasSetting = (name: SettingRequest) => {
		return this.findSetting(name) !== -1;
	}

	public getSettingOrDefault = <T>(name: SettingRequest, defaultValue: T): SqafSettingItem => {
		if (this.hasSetting(name)) {
			return this.getSetting(name);
		}

		let request = SqafSettingStorage.getSettingRequest(name);
		return new SqafSettingItem(request.name, request.marketerId, [defaultValue, defaultValue, defaultValue, defaultValue, defaultValue]);
	}

	public getSetting = (name: SettingRequest): SqafSettingItem => {
		let index = this.findSetting(name);
		if (index === -1) {
			throw new Error(`Could not find setting '${name}' in sqaf setting storage`);
		}

		return this.items()[index];
	}

	public loadSettingsWithCache = async (settings: SettingRequest[]) => {
		let notCached = settings.filter((item) => !this.hasSetting(item));
		await this.loadSettings(notCached);
	}

	public loadSettings = async (settings: SettingRequest[]) => {
		if (settings.length === 0) {
			return;
		}

		let request: ISettingRequest[] = settings.map(SqafSettingStorage.getSettingRequest);
		let response: ISettingResponse[] = await ServiceProvider.ajax.postJsonAsync({
			url: UrlHelper.getDataUrl("sqaf-setting"),
			params: { settings: request },
		}).catch(ErrorHandler.log());

		let oldItems = this.items();
		let newItem = response.map((item) => new SqafSettingItem(item.name, item.marketerId, item.values));

		for (let item of newItem) {
			let index = this.findSetting(item);
			if (index !== -1) {
				oldItems[index] = item;
			} else {
				oldItems.push(item);
			}
		}

		this.items(oldItems);

	}

	private findSetting(name: SettingRequest) {
		let request = SqafSettingStorage.getSettingRequest(name);
		return this.items().findIndex((val) => val && val.name === request.name && val.marketerId === request.marketerId);
	}

	private loadSettingsFromLocalStorage(): SqafSettingItem[] {
		let items: SqafSettingItem[] = ServiceProvider.cache.get(SqafSettingStorage.cacheKey);
		if (!items) {
			return [];
		}
		return items.map((item) => new SqafSettingItem(item.name, item.marketerId, item.values));
	}

	private storeSettingsIntoLocalStorage(items: SqafSettingItem[]) {
		ServiceProvider.cache.addOrUpdate(SqafSettingStorage.cacheKey, items);
	}
}